
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Candidate } from "@/types/candidate";
import { checkCVExists } from "@/utils/candidate/cv-storage";

// Define a type that explicitly includes ai_synthesis
interface SupabaseCandidate {
  id: string;
  name: string;
  email: string;
  phone?: string;
  function?: string;
  skills?: string[];
  match_score?: number;
  created_at?: string;
  updated_at?: string;
  manager?: string;
  status?: string;
  ai_analysis?: any;
  ai_synthesis?: string;
  embedding?: any;
  linkedin?: string; // Add LinkedIn field
}

export const useCandidateData = (id: string | undefined): {
  loading: boolean;
  candidate: Candidate | null;
  cv: string | null;
  cvExists: boolean;
  refetch: () => Promise<Candidate | null>;
} => {
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [cv, setCv] = useState<string | null>(null);
  const [cvExists, setCvExists] = useState(false);

  const fetchCandidateData = useCallback(async (): Promise<Candidate | null> => {

    if (!id) return null;


    try {
      console.log("Fetching candidate data for ID:", id);
      // Fetch candidate data from Supabase
      const { data, error } = await supabase
        .from('candidates')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error("Error fetching candidate:", error);
        throw error;
      }

      // Cast data to our helper type to handle ai_synthesis
      const candidateData = data as SupabaseCandidate;

      // Make sure ai_analysis is of the correct type
      const aiAnalysis = candidateData.ai_analysis ?
        (typeof candidateData.ai_analysis === 'object' ? candidateData.ai_analysis : {}) :
        undefined;

      // Convert the database data to match our Candidate type
      const candidate: Candidate = {
        id: candidateData.id,
        name: candidateData.name,
        email: candidateData.email,
        phone: candidateData.phone || undefined,
        function: candidateData.function || undefined,
        skills: candidateData.skills || [],
        match_score: candidateData.match_score || undefined,
        created_at: candidateData.created_at || undefined,
        updated_at: candidateData.updated_at || undefined,
        manager: candidateData.manager || undefined,
        status: candidateData.status || undefined,
        ai_analysis: aiAnalysis,
        ai_synthesis: candidateData.ai_synthesis || undefined,
        embedding: candidateData.embedding || undefined,
        linkedin: candidateData.linkedin || undefined // Include LinkedIn
      };

      console.log("Processed candidate with AI synthesis:", candidate.ai_synthesis);
      setCandidate(candidate);

      // Check if candidate has a CV
      try {

        const { data: resumeList, error: errorFetchResume } = await supabase.functions.invoke("get-candidate-resume", {
          body: {
            candidateId: id
          }
        });

        if (resumeList === null) {
          setCvExists(false);
          setCv(null);
          return candidate;
        }

        if (resumeList.error) {
          console.error("Error fetching resume list:", resumeList.error);
          throw new Error("Error fetching resume list");
        }

        console.log("Resume list fetched:", resumeList.resume.currentResume.publicUrl);

        setCvExists(true);
        setCv(resumeList.resume.currentResume.publicUrl);

        return candidate;
      } catch (error) {
        console.error("Error fetching candidate:", error);
        toast({
          title: "Error loading candidate",
          description: "Could not load candidate details",
          variant: "destructive",
        });
        return null;
      } finally {
        setLoading(false);
      }

    } catch (error) {
      console.error("Error in fetchCandidateData:", error);
      toast({
        title: "Error loading candidate",
        description: "Could not load candidate details",
        variant: "destructive",
      });
      setLoading(false);
      return null;
    }
  }, [id]);

  useEffect(() => {
    fetchCandidateData();
  }, [fetchCandidateData]);

  return {
    loading,
    candidate,
    cv,
    cvExists,
    refetch: fetchCandidateData
  };
};
